import {
  AccentBox,
  ActionGroup,
  Button,
  CodeBlock,
  ColumnLayout,
  ContextualHelp,
  ContextualHelpTrigger,
  Heading,
  InlineCode,
  Label,
  Link,
  NumberField,
  Section,
  Radio,
  RadioGroup,
  Flex,
  Text,
  Color,
  Header,
  Modal,
  Content,
  Action,
  Skeleton,
  useOverlayController,
  Alert,
} from "@mittwald/flow-remote-react-components";
import { useConfig } from "@mittwald/ext-bridge/react";
import { useProject } from "../hooks/useProject.ts";
import { useImageCompressionSettings } from "../hooks/useImageCompressionSettings.ts";
import { useUpdateImageCompressionSettings } from "../hooks/useUpdateImageCompressionSettings.ts";
import { useState } from "react";
import { UpdateImageCompressionSettingsError } from "../errors/UpdateImageCompressionSettingsError.ts";
import { useIsEmployee } from "@wiesinghilker/extension-lib-frontend";
import { useQueryClient } from "@tanstack/react-query";
import { useDeleteImageCompressionCache } from "../hooks/useDeleteImageCompressionCache.ts";
import { useImageCompressionCacheStats } from "../hooks/useImageCompressionCacheStats.ts";
import { useImageCompressionCacheFiles } from "../hooks/useImageCompressionCacheFiles.ts";
import { formatBytes } from "../utils/formatBytes.ts";

// Convert fileTypes array to format option string
const fileTypesToFormatOption = (
  fileTypes: ("jpg" | "jpeg" | "png")[],
): string => {
  const hasJpeg = fileTypes.includes("jpg") || fileTypes.includes("jpeg");
  const hasPng = fileTypes.includes("png");

  if (hasJpeg && hasPng) return "all";
  if (hasJpeg && !hasPng) return "jpeg";
  if (!hasJpeg && hasPng) return "png";
  return "all"; // fallback
};

// Convert format option string to fileTypes array
const formatOptionToFileTypes = (
  option: string,
): ("jpg" | "jpeg" | "png")[] => {
  switch (option) {
    case "all":
      return ["jpg", "jpeg", "png"];
    case "jpeg":
      return ["jpg", "jpeg"];
    case "png":
      return ["png"];
    case "disabled":
      return ["jpg", "jpeg", "png"]; // fallback, will be ignored when enabled=false
    default:
      return ["jpg", "jpeg", "png"];
  }
};

export const ImageCompression = () => {
  const isEmployee = useIsEmployee(import.meta.env.VITE_APP_API_BASE_URI);
  const config = useConfig();

  const appInstallationId = config.appInstallationId;
  if (!appInstallationId) {
    throw new Error("appInstallationId is not defined");
  }

  const projectId = config.projectId;
  if (!projectId) {
    throw new Error("projectId is not defined");
  }

  const project = useProject(projectId);
  const imageCompressionSettings = useImageCompressionSettings(
    projectId,
    appInstallationId,
  );
  const updateImageCompressionSettings = useUpdateImageCompressionSettings(
    projectId,
    appInstallationId,
  );
  const deleteCache = useDeleteImageCompressionCache(
    projectId,
    appInstallationId,
  );

  const [shouldLoadCacheStats, setShouldLoadCacheStats] = useState(false);
  const [shouldLoadCacheFiles, setShouldLoadCacheFiles] = useState(false);
  const queryClient = useQueryClient();

  const deleteCacheModalController = useOverlayController("Modal", {
    reuseControllerFromContext: false,
  });

  const cacheDetailsModalController = useOverlayController("Modal", {
    reuseControllerFromContext: false,
  });

  const cacheStats = useImageCompressionCacheStats(
    projectId,
    appInstallationId,
    shouldLoadCacheStats,
  );

  const cacheFiles = useImageCompressionCacheFiles(
    projectId,
    appInstallationId,
    shouldLoadCacheFiles,
  );

  const appInstallation = project.data.appInstallations.find(
    (a) => a.id == appInstallationId,
  );

  if (!appInstallation) {
    throw new Error("app installation not found in project");
  }

  // The persisted settings as the form understands them.
  const loadedCompressionLevel =
    imageCompressionSettings.data.compressionLevel || 75;
  const loadedFormatOption = imageCompressionSettings.data.enabled
    ? fileTypesToFormatOption(imageCompressionSettings.data.fileTypes)
    : "disabled";

  const [savedCompressionLevel, setSavedCompressionLevel] = useState<number>(
    loadedCompressionLevel,
  );
  const [savedFormatOption, setSavedFormatOption] =
    useState<string>(loadedFormatOption);

  // Draft states for unsaved changes
  const [draftCompressionLevel, setDraftCompressionLevel] = useState<number>(
    loadedCompressionLevel,
  );
  const [draftFormatOption, setDraftFormatOption] =
    useState<string>(loadedFormatOption);
  const [syncedSettings, setSyncedSettings] = useState(
    imageCompressionSettings.data,
  );

  // Refetched settings replace both saved and draft state. Done during render
  // instead of in an effect, see
  // https://react.dev/learn/you-might-not-need-an-effect.
  if (syncedSettings !== imageCompressionSettings.data) {
    setSyncedSettings(imageCompressionSettings.data);
    setSavedCompressionLevel(loadedCompressionLevel);
    setSavedFormatOption(loadedFormatOption);
    setDraftCompressionLevel(loadedCompressionLevel);
    setDraftFormatOption(loadedFormatOption);
  }

  const hasUnsavedChanges =
    draftCompressionLevel !== savedCompressionLevel ||
    draftFormatOption !== savedFormatOption;

  const [errorOnSubmit, setErrorOnSubmit] = useState<Error | undefined>(
    undefined,
  );

  if (errorOnSubmit) {
    throw errorOnSubmit;
  }

  // Rendered after all hooks so the hook order stays stable across renders.
  if (!appInstallation.phpVersion) {
    return (
      <Section>
        <Alert status="info">
          <Heading>Kein PHP in deiner App gefunden</Heading>
          <Content>
            Diese Extension benötigt PHP, um Bilder komprimieren zu können.
            Wechsle bitte zu einer PHP-App oder installiere PHP über den Tab
            „Software“ in dieser App.
          </Content>
        </Alert>
      </Section>
    );
  }

  const handleSaveSettings = async () => {
    const isEnabled = draftFormatOption !== "disabled";

    const result = await updateImageCompressionSettings.mutateAsync({
      enabled: isEnabled,
      compressionLevel: draftCompressionLevel,
      fileTypes: formatOptionToFileTypes(draftFormatOption),
    });

    if (result.error) {
      setErrorOnSubmit(
        new UpdateImageCompressionSettingsError(result.error.message),
      );
      return;
    }

    setSavedCompressionLevel(draftCompressionLevel);
    setSavedFormatOption(draftFormatOption);
  };

  const handleCancelSettings = () => {
    setDraftCompressionLevel(savedCompressionLevel);
    setDraftFormatOption(savedFormatOption);
  };

  const handleDeleteCache = async () => {
    await deleteCache.mutateAsync();
    // Modal schließen bei Erfolg
    deleteCacheModalController.close();
    // Invalidierung nach Modal-Schließen
    await queryClient.invalidateQueries({
      queryKey: [
        "imageCompressionCacheStats",
        { projectId, appInstallationId },
      ],
    });
    await queryClient.invalidateQueries({
      queryKey: [
        "imageCompressionCacheFiles",
        { projectId, appInstallationId },
      ],
    });
  };

  return (
    <Section>
      <Text>
        Reduziere die Dateigröße deiner Bilder automatisch durch
        WebP-Komprimierung, um die Ladegeschwindigkeit deiner Website deutlich
        zu verbessern. Die Umwandlung erfolgt direkt auf dem Server beim
        Ausliefern der Bilder – ganz ohne Plugin oder manuelle Optimierung.
      </Text>

      <Section>
        <Header>
          <Heading>Einstellungen</Heading>
          <Button
            variant="soft"
            color="danger"
            onPress={() => {
              // Reset cache stats state to trigger fresh loading with skeleton
              setShouldLoadCacheStats(false);
              queryClient.removeQueries({
                queryKey: [
                  "imageCompressionCacheStats",
                  { projectId, appInstallationId },
                ],
              });
              setShouldLoadCacheStats(true);
              deleteCacheModalController.open();
            }}
          >
            Generierte Bilder löschen
          </Button>
        </Header>

        <RadioGroup
          value={draftFormatOption}
          onChange={(value) => {
            setDraftFormatOption(value);
          }}
          isDisabled={false}
        >
          <Label>Bildkomprimierung</Label>
          <Radio value="disabled">Deaktiviert</Radio>
          <Radio value="all">Aktiv für JPG & PNG</Radio>
          <Radio value="jpeg">Aktiv nur für JPG</Radio>
          <Radio value="png">Aktiv nur für PNG</Radio>
        </RadioGroup>

        {draftFormatOption !== "disabled" && (
          <ColumnLayout rowGap="s" columnGap="l">
            <NumberField
              minValue={1}
              maxValue={100}
              value={draftCompressionLevel}
              onChange={(value) => {
                setDraftCompressionLevel(value);
              }}
              isRequired
              isDisabled={false}
              formatOptions={{
                style: "unit",
                unit: "percent",
              }}
            >
              <Label>
                WebP-Qualitätslevel
                <ContextualHelpTrigger>
                  <Button />
                  <ContextualHelp>
                    <Heading>WebP-Qualitätslevel</Heading>
                    <Text>
                      <ul>
                        <li>
                          <strong>Hohe Qualität (85–100%)</strong>: Minimale
                          WebP-Komprimierung – ideal für hochwertige Fotos oder
                          visuell anspruchsvolle Bilder.
                        </li>
                        <li>
                          <strong>Mittlere Qualität (60–85%)</strong>: Guter
                          Kompromiss zwischen Dateigröße und Bildqualität –
                          empfohlen für die meisten Web-Bilder. (Standardwert:
                          75%)
                        </li>
                        <li>
                          <strong>Niedrige Qualität (30–60%)</strong>: Stärkere
                          WebP-Komprimierung – geeignet für Thumbnails oder
                          weniger relevante Bilder.
                        </li>
                      </ul>
                    </Text>
                    <Text>
                      Die <InlineCode>Qualitätsstufe</InlineCode> bestimmt das
                      Verhältnis zwischen Komprimierungsrate und sichtbarer
                      Bildqualität. Je niedriger der Wert, desto kleiner die
                      Datei – jedoch mit potenziell sichtbaren
                      Qualitätsverlusten.
                    </Text>
                    <Link
                      href="https://developers.google.com/speed/webp?hl=de"
                      target="_blank"
                    >
                      Mehr über WebP-Bildoptimierung erfahren
                    </Link>
                  </ContextualHelp>
                </ContextualHelpTrigger>
              </Label>
            </NumberField>
          </ColumnLayout>
        )}

        {hasUnsavedChanges && (
          <ActionGroup>
            <Action onAction={handleSaveSettings}>
              <Button color="success">Speichern</Button>
            </Action>
            <Button
              color="secondary"
              slot="secondary"
              variant="soft"
              onPress={handleCancelSettings}
            >
              Zurücksetzen
            </Button>
          </ActionGroup>
        )}
      </Section>

      {isEmployee === false && (
        <AccentBox backgroundColor="gradient">
          <Flex align="center" wrap="wrap" gap="m">
            <Flex direction="column" grow>
              <Heading size="l">
                <Color color="blue">
                  Kennst du schon unsere anderen Extensions?
                </Color>
              </Heading>
              <Text>
                <strong>
                  Jetzt{" "}
                  <Color color="violet">
                    Verzeichnisse mit Passwort schützen
                  </Color>{" "}
                  und <Color color="violet">PHP-Einstellungen ändern</Color>.
                </strong>
              </Text>
            </Flex>
            <Link
              target="_blank"
              href="https://studio.mittwald.de/app/marketplace/marketplace/ddaeea02-a622-4a51-ba2e-e1a102c3dfab"
              color="dark"
            >
              PHP-Einstellungen
            </Link>
            <Link
              target="_blank"
              href="https://studio.mittwald.de/app/marketplace/marketplace/ede29562-9a5b-4bee-81c3-479e878bf259"
              color="dark"
            >
              Passwortschutz
            </Link>
          </Flex>
        </AccentBox>
      )}

      <Modal controller={deleteCacheModalController}>
        <Heading>Generierte WebP-Bilder löschen</Heading>
        <Content>
          <Section>
            {cacheStats.isLoading ? (
              <Skeleton width="100%" height="100px" />
            ) : cacheStats.data ? (
              cacheStats.data.count === 0 ? (
                <Alert status="info">
                  <Heading>Keine Dateien gefunden</Heading>
                  <Content>
                    Die Extension hat noch keine Bilder generiert. Wenn du deine
                    Website aufrufst, werden die Bilder automatisch erstellt und
                    im komprimierten WebP-Format gespeichert.
                  </Content>
                </Alert>
              ) : (
                <>
                  <Text>
                    Möchtest du wirklich alle durch die Extension erstellten
                    WebP-Bilder aus der App löschen?
                  </Text>
                  <Text>
                    <strong>
                      {cacheStats.data.count}{" "}
                      {cacheStats.data.count === 1 ? "Datei" : "Dateien"} (
                      {formatBytes(cacheStats.data.totalSizeBytes)})
                      {cacheStats.data.count === 1 ? " wird" : " werden"}{" "}
                      gelöscht:
                    </strong>
                  </Text>
                  <Button
                    variant="soft"
                    color="secondary"
                    size="s"
                    onPress={() => {
                      // Load cache files when details button is clicked
                      setShouldLoadCacheFiles(false);
                      queryClient.removeQueries({
                        queryKey: [
                          "imageCompressionCacheFiles",
                          { projectId, appInstallationId },
                        ],
                      });
                      setShouldLoadCacheFiles(true);
                      cacheDetailsModalController.open();
                    }}
                  >
                    Dateiliste anzeigen
                  </Button>
                </>
              )
            ) : (
              <Text>Cache-Informationen konnten nicht geladen werden.</Text>
            )}
          </Section>
        </Content>
        <ActionGroup>
          <Button
            color="danger"
            isDisabled={
              cacheStats.isLoading ||
              !cacheStats.data ||
              cacheStats.data.count === 0
            }
            isPending={deleteCache.isPending}
            onPress={() => void handleDeleteCache()}
          >
            Bilder löschen
          </Button>
          <Action
            onAction={() => {
              deleteCacheModalController.close();
            }}
          >
            <Button variant="soft" color="secondary">
              Abbrechen
            </Button>
          </Action>
        </ActionGroup>
      </Modal>

      <Modal controller={cacheDetailsModalController} size="m" offCanvas>
        <Heading>Auszug der generierten WebP-Dateien</Heading>
        <Content>
          <Section>
            {cacheFiles.isLoading ? (
              <>
                <Text>Lade Auszug der Dateiliste...</Text>
                <Skeleton />
              </>
            ) : cacheFiles.data && cacheFiles.data.files.length > 0 ? (
              <>
                <Text>
                  Um dich vor dem Löschen der generierten WebP-Bilder zu
                  vergewissern, siehst du hier einen Auszug (max. 250) der
                  aktuell komprimierten Dateien:
                </Text>
                <CodeBlock
                  language="text"
                  code={cacheFiles.data.files.join("\n")}
                />
              </>
            ) : (
              <Text>Keine Cache-Dateien gefunden.</Text>
            )}
          </Section>
        </Content>
        <ActionGroup>
          <Action
            onAction={() => {
              cacheDetailsModalController.close();
            }}
          >
            <Button variant="soft" color="secondary">
              Schließen
            </Button>
          </Action>
        </ActionGroup>
      </Modal>
    </Section>
  );
};
